import React, { useState, Children } from 'react';
import { PlusIcon, SearchIcon, EyeIcon, DownloadIcon, FileSpreadsheetIcon, FileTextIcon, FileDownIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { ClinicalHistory as ClinicalHistoryType } from '../../types';
import { formatDate } from '../../utils/helpers';
import { exportClinicalHistoriesToExcel } from '../../utils/excelExport';
import { generateClinicalHistoryPdf } from '../../utils/clinicalHistoryPdf';
import { FormSection } from '../ui/FormSection';
export function ClinicalHistory() {
  const {
    patients,
    getPatient,
    doctors,
    clinicalHistories,
    addClinicalHistory
  } = useData();
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // Form state - Antecedentes Personales
  const [personalData, setPersonalData] = useState({
    fullName: '',
    birthDate: '',
    address: '',
    phone: '',
    email: '',
    age: 0,
    gender: '',
    occupation: '',
    numberOfChildren: 0,
    deliveryType: '',
    alcohol: '',
    tobacco: '',
    drugs: '',
    infusions: '',
    consultationReason: ''
  });
  // Form state - Antecedentes Clínicos
  const [clinicalData, setClinicalData] = useState({
    diseases: '',
    currentMedications: '',
    chronicDiseases: '',
    surgicalHistory: '',
    implantsOrDevices: '',
    implantType: '',
    allergies: ''
  });
  // Form state - Diagnóstico, Tratamiento, etc.
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [medicalIndications, setMedicalIndications] = useState('');
  const [sessions, setSessions] = useState('');
  const [sessionNumber, setSessionNumber] = useState('');
  const [comments, setComments] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Character limits for print-safe fields (priority order)
  const CHAR_LIMITS = {
    diagnosis: 500,
    treatment: 1000,
    medicalIndications: 500,
    comments: 300
  };
  // Today's date for max restriction
  const today = new Date().toISOString().split('T')[0];
  const filteredHistories = clinicalHistories.filter(h => {
    const patient = getPatient(h.patientId);
    return patient?.name.toLowerCase().includes(searchTerm.toLowerCase());
  });
  const patientOptions = patients.map(p => ({
    value: p.id,
    label: p.name
  }));
  const doctorOptions = doctors.map(d => ({
    value: d.id,
    label: `Dr. ${d.name}`
  }));
  const openNewForm = () => {
    setSelectedHistory(null);
    setSelectedPatientId('');
    setSelectedDoctorId('');
    setSaveError('');
    resetForm();
    setIsFormOpen(true);
  };
  const openViewHistory = (history: ClinicalHistoryType) => {
    setSelectedHistory(history);
    setIsViewOpen(true);
  };
  const resetForm = () => {
    setPersonalData({
      fullName: '',
      birthDate: '',
      address: '',
      phone: '',
      email: '',
      age: 0,
      gender: '',
      occupation: '',
      numberOfChildren: 0,
      deliveryType: '',
      alcohol: '',
      tobacco: '',
      drugs: '',
      infusions: '',
      consultationReason: ''
    });
    setClinicalData({
      diseases: '',
      currentMedications: '',
      chronicDiseases: '',
      surgicalHistory: '',
      implantsOrDevices: '',
      implantType: '',
      allergies: ''
    });
    setDiagnosis('');
    setTreatment('');
    setMedicalIndications('');
    setSessions('');
    setSessionNumber('');
    setComments('');
    setFormErrors({});
  };
  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    const patient = getPatient(patientId);
    if (patient) {
      setPersonalData({
        fullName: patient.name,
        birthDate: '',
        address: patient.address,
        phone: patient.phone,
        email: patient.email,
        age: patient.age,
        gender: patient.gender,
        occupation: '',
        numberOfChildren: 0,
        deliveryType: '',
        alcohol: '',
        tobacco: '',
        drugs: '',
        infusions: '',
        consultationReason: ''
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (!selectedDoctorId) errors.doctor = 'Debe seleccionar un doctor';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setIsSaving(true);
    setSaveError('');
    const selectedDoctor = doctors.find(d => d.id === selectedDoctorId);
    try {
      await addClinicalHistory({
        patientId: selectedPatientId,
        date: new Date(),
        doctorId: selectedDoctorId,
        doctorName: selectedDoctor?.name || user?.name || '',
        personalBackground: personalData,
        clinicalBackground: clinicalData,
        diagnosis,
        treatment,
        medicalIndications,
        sessions,
        sessionNumber,
        comments,
        doctorSignature: selectedDoctor?.name || user?.name || ''
      });
      setIsFormOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('❌ Error guardando historial clínico:', error);
      setSaveError(error?.message || 'Error al guardar el historial clínico. Verifica la consola para más detalles.');
    } finally {
      setIsSaving(false);
    }
  };
  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportClinicalHistoriesToExcel(clinicalHistories, (patientId: string) => {
      const patient = getPatient(patientId);
      return patient?.name || '-';
    });
    setIsExporting(false);
  };
  const handleDownloadPdf = async (history: ClinicalHistoryType) => {
    setIsGeneratingPdf(true);
    try {
      const patient = getPatient(history.patientId);
      await generateClinicalHistoryPdf(history, patient?.name);
    } catch (error) {
      console.error('Error generando PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-348">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-349">
        <div data-id="element-350">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-351">
            Historial Clínico
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-352">
            Gestión de historiales clínicos de pacientes
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-354" />} onClick={openNewForm} data-id="element-353">
          Nuevo Historial
        </Button>
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 border-violet-200 dark:border-violet-800" data-id="element-355">
          <div className="flex items-center justify-between" data-id="element-356">
            <div className="flex items-center gap-4" data-id="element-357">
              <div className="w-14 h-14 rounded-xl bg-violet-500 dark:bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-500/20" data-id="element-358">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-359" />
              </div>
              <div data-id="element-360">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-361">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-362">
                  Descarga todos los historiales clínicos en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-363">
                  <div className="flex items-center gap-1.5 text-sm text-violet-700 dark:text-violet-400" data-id="element-364">
                    <FileTextIcon className="w-4 h-4" data-id="element-365" />
                    <span className="font-medium" data-id="element-366">
                      {clinicalHistories.length} historiales
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-367">
                    • Incluye: Paciente, Doctor, Diagnóstico, Tratamiento
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-369" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-violet-500/20 hover:shadow-xl hover:shadow-violet-500/30 transition-all duration-200" data-id="element-368">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Search */}
      <Card data-id="element-370">
        <div className="relative" data-id="element-371">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-372" />
          <Input placeholder="Buscar por paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-373" />
        </div>
      </Card>

      {/* Histories List */}
      <Card padding="none" data-id="element-374">
        <div className="overflow-x-auto" data-id="element-375">
          <table className="w-full" data-id="element-376">
            <thead data-id="element-377">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-378">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-379">
                  Fecha
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-380">
                  Paciente
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-381">
                  Doctor
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-382">
                  Motivo de Consulta
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-383">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-384">
              {filteredHistories.length === 0 ? <tr data-id="element-385">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-386">
                    No hay historiales clínicos registrados
                  </td>
                </tr> : filteredHistories.map(history => {
              const patient = getPatient(history.patientId);
              return <tr key={history.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-387">
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-388">
                        {formatDate(history.date)}
                      </td>
                      <td className="py-4 px-6" data-id="element-389">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-390">
                          {patient?.name}
                        </p>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-391">
                        Dr. {history.doctorName}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400" data-id="element-392">
                        {history.personalBackground.consultationReason || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-393">
                        <div className="flex items-center justify-end gap-2" data-id="element-394">
                          <button onClick={() => openViewHistory(history)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver historial" data-id="element-395">
                            <EyeIcon className="w-4 h-4" data-id="element-396" />
                          </button>
                          <button onClick={() => handleDownloadPdf(history)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-397">
                            <FileDownIcon className="w-4 h-4" data-id="element-398" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nuevo Historial Clínico (Medicina Estética)" size="full" icon={<FileTextIcon className="w-6 h-6" data-id="element-400" />} accentColor="emerald" data-id="element-399">
        <form onSubmit={handleSubmit} className="space-y-8 pb-8" data-id="element-401">
          {saveError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-402">
              ❌ {saveError}
            </div>}

          {/* Selección de Paciente y Doctor */}
          <FormSection title="Paciente y Doctor" data-id="element-403">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-404">
              <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={handlePatientSelect} error={formErrors.patient} searchable placeholder="Buscar paciente..." data-id="element-405" />
              <Select label="Doctor Responsable" options={doctorOptions} value={selectedDoctorId} onChange={setSelectedDoctorId} error={formErrors.doctor} searchable placeholder="Seleccionar doctor..." data-id="element-406" />
            </div>
          </FormSection>

          {/* Antecedentes Personales */}
          <FormSection title="Antecedentes Personales" data-id="element-407">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-408">
              <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
              ...personalData,
              fullName: e.target.value
            })} error={formErrors.fullName} data-id="element-409" />
              <Input type="date" label="Fecha de Nacimiento (F.N.)" value={personalData.birthDate} onChange={e => setPersonalData({
              ...personalData,
              birthDate: e.target.value
            })} max={today} data-id="element-410" />
              <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
              ...personalData,
              address: e.target.value
            })} data-id="element-411" />
              <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
              ...personalData,
              phone: e.target.value
            })} data-id="element-412" />
              <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
              ...personalData,
              email: e.target.value
            })} data-id="element-413" />
              <Input type="number" label="Edad" value={personalData.age} onChange={e => setPersonalData({
              ...personalData,
              age: parseInt(e.target.value) || 0
            })} data-id="element-414" />
              <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
              ...personalData,
              gender: e.target.value
            })} data-id="element-415" />
              <Input label="Ocupación u Oficio" value={personalData.occupation} onChange={e => setPersonalData({
              ...personalData,
              occupation: e.target.value
            })} data-id="element-416" />
              <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
              ...personalData,
              numberOfChildren: parseInt(e.target.value) || 0
            })} data-id="element-417" />
              <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
              ...personalData,
              deliveryType: e.target.value
            })} placeholder="Ej: C/A, Normal" data-id="element-418" />
              <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
              ...personalData,
              alcohol: e.target.value
            })} data-id="element-419" />
              <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
              ...personalData,
              tobacco: e.target.value
            })} data-id="element-420" />
              <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
              ...personalData,
              drugs: e.target.value
            })} data-id="element-421" />
              <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
              ...personalData,
              infusions: e.target.value
            })} data-id="element-422" />
              <div className="md:col-span-3" data-id="element-423">
                <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                ...personalData,
                consultationReason: e.target.value
              })} rows={2} data-id="element-424" />
              </div>
            </div>
          </FormSection>

          {/* Antecedentes Clínicos */}
          <FormSection title="Antecedentes Clínicos" data-id="element-425">
            <div className="grid grid-cols-1 gap-4" data-id="element-426">
              <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
              ...clinicalData,
              diseases: e.target.value
            })} rows={2} data-id="element-427" />
              <Textarea label="Medicamentos que Toma Actualmente" value={clinicalData.currentMedications} onChange={e => setClinicalData({
              ...clinicalData,
              currentMedications: e.target.value
            })} rows={2} data-id="element-428" />
              <Textarea label="Antecedentes Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
              ...clinicalData,
              chronicDiseases: e.target.value
            })} rows={2} data-id="element-429" />
              <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
              ...clinicalData,
              surgicalHistory: e.target.value
            })} rows={2} data-id="element-430" />
              <div className="grid grid-cols-2 gap-4" data-id="element-431">
                <Input label="Uso de Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                ...clinicalData,
                implantsOrDevices: e.target.value
              })} data-id="element-432" />
                <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                ...clinicalData,
                implantType: e.target.value
              })} data-id="element-433" />
              </div>
              <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
              ...clinicalData,
              allergies: e.target.value
            })} rows={2} data-id="element-434" />
            </div>
          </FormSection>

          {/* Diagnóstico */}
          <FormSection title="Diagnóstico" data-id="element-435">
            <Textarea value={diagnosis} onChange={e => {
            if (e.target.value.length <= CHAR_LIMITS.diagnosis) {
              setDiagnosis(e.target.value);
            }
          }} rows={6} placeholder="Escriba el diagnóstico..." maxLength={CHAR_LIMITS.diagnosis} data-id="element-436" />
            <p className={`mt-1 text-xs text-right ${diagnosis.length >= CHAR_LIMITS.diagnosis ? 'text-red-500 font-medium' : diagnosis.length >= CHAR_LIMITS.diagnosis * 0.85 ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-437">
              {diagnosis.length}/{CHAR_LIMITS.diagnosis} caracteres
            </p>
          </FormSection>

          {/* Tratamiento */}
          <FormSection title="Tratamiento" data-id="element-438">
            <Textarea value={treatment} onChange={e => {
            if (e.target.value.length <= CHAR_LIMITS.treatment) {
              setTreatment(e.target.value);
            }
          }} rows={10} placeholder="Escriba el tratamiento detallado..." maxLength={CHAR_LIMITS.treatment} data-id="element-439" />
            <p className={`mt-1 text-xs text-right ${treatment.length >= CHAR_LIMITS.treatment ? 'text-red-500 font-medium' : treatment.length >= CHAR_LIMITS.treatment * 0.85 ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-440">
              {treatment.length}/{CHAR_LIMITS.treatment} caracteres
            </p>
          </FormSection>

          {/* Indicaciones Médicas */}
          <FormSection title="Indicaciones Médicas" data-id="element-441">
            <Textarea value={medicalIndications} onChange={e => {
            if (e.target.value.length <= CHAR_LIMITS.medicalIndications) {
              setMedicalIndications(e.target.value);
            }
          }} rows={6} placeholder="Escriba las indicaciones médicas para el paciente..." maxLength={CHAR_LIMITS.medicalIndications} data-id="element-442" />
            <p className={`mt-1 text-xs text-right ${medicalIndications.length >= CHAR_LIMITS.medicalIndications ? 'text-red-500 font-medium' : medicalIndications.length >= CHAR_LIMITS.medicalIndications * 0.85 ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-443">
              {medicalIndications.length}/{CHAR_LIMITS.medicalIndications}{' '}
              caracteres
            </p>
          </FormSection>

          {/* Sesiones */}
          <FormSection title="Sesiones" data-id="element-444">
            <div className="grid grid-cols-2 gap-4" data-id="element-445">
              <Input label="Sesiones" value={sessions} onChange={e => setSessions(e.target.value)} placeholder="Descripción de sesiones" data-id="element-446" />
              <Input label="N° de Sesión" value={sessionNumber} onChange={e => setSessionNumber(e.target.value)} placeholder="Número de sesión" data-id="element-447" />
            </div>
          </FormSection>

          {/* Comentarios */}
          <FormSection title="Comentarios" data-id="element-448">
            <Textarea value={comments} onChange={e => {
            if (e.target.value.length <= CHAR_LIMITS.comments) {
              setComments(e.target.value);
            }
          }} rows={3} placeholder="Comentarios adicionales..." maxLength={CHAR_LIMITS.comments} data-id="element-449" />
            <p className={`mt-1 text-xs text-right ${comments.length >= CHAR_LIMITS.comments ? 'text-red-500 font-medium' : comments.length >= CHAR_LIMITS.comments * 0.85 ? 'text-amber-500' : 'text-gray-400'}`} data-id="element-450">
              {comments.length}/{CHAR_LIMITS.comments} caracteres
            </p>
          </FormSection>

          {/* Firma del Doctor */}
          <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-451">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-452">
              Firma del Dr. (a)
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-453">
              {user?.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-454">
              Fecha: {formatDate(new Date())}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 p-4 -mx-4 -mb-4 shadow-lg" data-id="element-455">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-456">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSaving} data-id="element-457">
              {isSaving ? 'Guardando...' : 'Guardar Historial Clínico'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal with Print-Optimized Layout */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Historial Clínico (Medicina Estética)" size="full" data-id="element-458">
        {selectedHistory && <div className="print-clinical-history" data-id="element-459">
            {/* Print Header - repeats on every printed page via table-header-group */}
            <div className="print-header hidden print:block" data-id="element-460">
              <div className="print-header-inner" data-id="element-461">
                <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Logo" data-id="element-462" />
                <div data-id="element-463">
                  <h1 data-id="element-464">HISTORIA CLÍNICA</h1>
                  <p data-id="element-465">Savidna - Centro de Bienestar y Belleza</p>
                </div>
                <div className="print-date" data-id="element-466">
                  Fecha: {formatDate(selectedHistory.date)}
                </div>
              </div>
            </div>

            {/* Screen Header (visible on screen, hidden on print) */}
            <div className="print:hidden flex items-center justify-between pb-4 border-b-2 border-sky-500 mb-6" data-id="element-467">
              <div className="flex items-center gap-4" data-id="element-468">
                <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-16 h-16 rounded-full" data-id="element-469" />
                <div data-id="element-470">
                  <h2 className="text-2xl font-bold text-sky-600 dark:text-sky-400" data-id="element-471">
                    HISTORIA CLÍNICA
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-472">
                    Savidna - Centro de Bienestar y Belleza
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-473">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-474">
                  Fecha:
                </p>
                <p className="font-semibold text-gray-900 dark:text-white" data-id="element-475">
                  {formatDate(selectedHistory.date)}
                </p>
              </div>
            </div>

            {/* Print Body - wraps all content sections */}
            <div className="print-body" data-id="element-476">
              {/* Patient Info Box */}
              <div className="print-patient-info mb-6" data-id="element-477">
                <div data-id="element-478">
                  <strong data-id="element-479">Paciente:</strong>{' '}
                  {selectedHistory.personalBackground.fullName}
                </div>
                <div data-id="element-480">
                  <strong data-id="element-481">Edad:</strong>{' '}
                  {selectedHistory.personalBackground.age} años
                </div>
                <div data-id="element-482">
                  <strong data-id="element-483">Sexo:</strong>{' '}
                  {selectedHistory.personalBackground.gender}
                </div>
                <div data-id="element-484">
                  <strong data-id="element-485">Teléfono:</strong>{' '}
                  {selectedHistory.personalBackground.phone}
                </div>
                <div data-id="element-486">
                  <strong data-id="element-487">Email:</strong>{' '}
                  {selectedHistory.personalBackground.email}
                </div>
                <div data-id="element-488">
                  <strong data-id="element-489">Fecha:</strong> {formatDate(selectedHistory.date)}
                </div>
              </div>

              {/* Content sections */}
              <div className="space-y-6" data-id="element-490">
                {/* Antecedentes Personales */}
                <div className="print-section" data-id="element-491">
                  <h2 data-id="element-492">Antecedentes Personales</h2>
                  <div className="print-field-group" data-id="element-493">
                    <div className="print-field" data-id="element-494">
                      <div className="print-field-label" data-id="element-495">Ocupación:</div>
                      <div className="print-field-value" data-id="element-496">
                        {selectedHistory.personalBackground.occupation || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-497">
                      <div className="print-field-label" data-id="element-498">N° de hijos:</div>
                      <div className="print-field-value" data-id="element-499">
                        {selectedHistory.personalBackground.numberOfChildren}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-500">
                      <div className="print-field-label" data-id="element-501">Tipo de parto:</div>
                      <div className="print-field-value" data-id="element-502">
                        {selectedHistory.personalBackground.deliveryType || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-503">
                      <div className="print-field-label" data-id="element-504">Alcohol:</div>
                      <div className="print-field-value" data-id="element-505">
                        {selectedHistory.personalBackground.alcohol || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-506">
                      <div className="print-field-label" data-id="element-507">Tabaco:</div>
                      <div className="print-field-value" data-id="element-508">
                        {selectedHistory.personalBackground.tobacco || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-509">
                      <div className="print-field-label" data-id="element-510">Drogas:</div>
                      <div className="print-field-value" data-id="element-511">
                        {selectedHistory.personalBackground.drugs || '-'}
                      </div>
                    </div>
                    <div className="print-field print-field-full" data-id="element-512">
                      <div className="print-field-label" data-id="element-513">Infusiones:</div>
                      <div className="print-field-value" data-id="element-514">
                        {selectedHistory.personalBackground.infusions || '-'}
                      </div>
                    </div>
                    <div className="print-field print-field-full" data-id="element-515">
                      <div className="print-field-label" data-id="element-516">
                        Motivo de la consulta:
                      </div>
                      <div className="print-field-value" data-id="element-517">
                        {selectedHistory.personalBackground.consultationReason || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Antecedentes Clínicos */}
                <div className="print-section" data-id="element-518">
                  <h2 data-id="element-519">Antecedentes Clínicos</h2>
                  <div className="space-y-3" data-id="element-520">
                    <div className="print-field" data-id="element-521">
                      <div className="print-field-label" data-id="element-522">Enfermedades:</div>
                      <div className="print-field-value" data-id="element-523">
                        {selectedHistory.clinicalBackground.diseases || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-524">
                      <div className="print-field-label" data-id="element-525">
                        Medicamentos actuales:
                      </div>
                      <div className="print-field-value" data-id="element-526">
                        {selectedHistory.clinicalBackground.currentMedications || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-527">
                      <div className="print-field-label" data-id="element-528">
                        Enfermedades crónicas:
                      </div>
                      <div className="print-field-value" data-id="element-529">
                        {selectedHistory.clinicalBackground.chronicDiseases || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-530">
                      <div className="print-field-label" data-id="element-531">
                        Antecedentes quirúrgicos:
                      </div>
                      <div className="print-field-value" data-id="element-532">
                        {selectedHistory.clinicalBackground.surgicalHistory || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-533">
                      <div className="print-field-label" data-id="element-534">
                        Implantes/Dispositivos:
                      </div>
                      <div className="print-field-value" data-id="element-535">
                        {selectedHistory.clinicalBackground.implantsOrDevices || '-'}
                        {selectedHistory.clinicalBackground.implantType && ` - Tipo: ${selectedHistory.clinicalBackground.implantType}`}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-536">
                      <div className="print-field-label" data-id="element-537">Alergias:</div>
                      <div className="print-field-value" data-id="element-538">
                        {selectedHistory.clinicalBackground.allergies || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Diagnóstico */}
                <div className="print-section" data-id="element-539">
                  <h2 data-id="element-540">Diagnóstico</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-541">
                    {selectedHistory.diagnosis || '-'}
                  </div>
                </div>

                {/* Tratamiento */}
                <div className="print-section" data-id="element-542">
                  <h2 data-id="element-543">Tratamiento</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-544">
                    {selectedHistory.treatment || '-'}
                  </div>
                </div>

                {/* Indicaciones Médicas */}
                <div className="print-section" data-id="element-545">
                  <h2 data-id="element-546">Indicaciones Médicas</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-547">
                    {selectedHistory.medicalIndications || '-'}
                  </div>
                </div>

                {/* Sesiones */}
                <div className="print-section" data-id="element-548">
                  <h2 data-id="element-549">Sesiones</h2>
                  <div className="print-field-group" data-id="element-550">
                    <div className="print-field" data-id="element-551">
                      <div className="print-field-label" data-id="element-552">Sesiones:</div>
                      <div className="print-field-value" data-id="element-553">
                        {selectedHistory.sessions || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-554">
                      <div className="print-field-label" data-id="element-555">N° de sesión:</div>
                      <div className="print-field-value" data-id="element-556">
                        {selectedHistory.sessionNumber || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comentarios */}
                <div className="print-section" data-id="element-557">
                  <h2 data-id="element-558">Comentarios</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-559">
                    {selectedHistory.comments || '-'}
                  </div>
                </div>

                {/* Firma */}
                <div className="print-signature" data-id="element-560">
                  <div className="print-signature-line" data-id="element-561">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-562">
                      {selectedHistory.doctorSignature}
                    </p>
                  </div>
                  <p data-id="element-563">Firma del Dr. (a)</p>
                </div>
              </div>

              {/* Actions (hidden on print) */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 no-print print:hidden" data-id="element-564">
                <Button variant="outline" leftIcon={<FileDownIcon className="w-4 h-4" data-id="element-566" />} onClick={() => selectedHistory && handleDownloadPdf(selectedHistory)} isLoading={isGeneratingPdf} data-id="element-565">
                  {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
                </Button>
                <Button onClick={() => setIsViewOpen(false)} data-id="element-567">Cerrar</Button>
              </div>
            </div>
          </div>}
      </Modal>
    </div>;
}